/**
 * Analytics Hooks
 *
 * React Query hooks for analytics and metrics data.
 */
import { useQuery } from '@tanstack/react-query';
import { analyticsService } from '../services/analytics';
import { AnalyticsQueryParams, TimeRange } from '../types/analytics';

// Query keys for analytics
export const analyticsKeys = {
  all: ['analytics'] as const,
  dashboard: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'dashboard', params] as const,
  summary: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'summary', params] as const,
  executions: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'executions', params] as const,
  executionTrend: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'executionTrend', params] as const,
  costs: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'costs', params] as const,
  costTrend: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'costTrend', params] as const,
  agents: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'agents', params] as const,
  costByAgent: (params?: AnalyticsQueryParams) =>
    [...analyticsKeys.all, 'costByAgent', params] as const,
  recentExecutions: (limit?: number) =>
    [...analyticsKeys.all, 'recentExecutions', limit] as const,
};

/**
 * Hook to fetch analytics dashboard data
 *
 * @param params - Query parameters including time range
 * @returns Query result with dashboard data
 */
export function useAnalyticsDashboard(params?: AnalyticsQueryParams) {
  return useQuery({
    queryKey: analyticsKeys.dashboard(params),
    queryFn: () => analyticsService.getDashboard(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch analytics summary
 *
 * @param params - Query parameters
 * @returns Query result with summary data
 */
export function useAnalyticsSummary(params?: AnalyticsQueryParams) {
  return useQuery({
    queryKey: analyticsKeys.summary(params),
    queryFn: () => analyticsService.getSummary(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch execution analytics
 *
 * @param params - Query parameters
 * @returns Query result with execution analytics
 */
export function useExecutionAnalytics(
  params?: AnalyticsQueryParams,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: analyticsKeys.executions(params),
    queryFn: () => analyticsService.getExecutionAnalytics(params),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Hook to fetch cost analytics
 *
 * @param params - Query parameters
 * @returns Query result with cost analytics
 */
export function useCostAnalytics(
  params?: AnalyticsQueryParams,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: analyticsKeys.costs(params),
    queryFn: () => analyticsService.getCostAnalytics(params),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Hook to fetch execution trend
 *
 * @param params - Query parameters
 * @returns Query result with execution trend
 */
export function useExecutionTrend(params?: AnalyticsQueryParams) {
  return useQuery({
    queryKey: analyticsKeys.executionTrend(params),
    queryFn: () => analyticsService.getExecutionTrend(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch cost trend
 *
 * @param params - Query parameters
 * @returns Query result with cost trend
 */
export function useCostTrend(params?: AnalyticsQueryParams) {
  return useQuery({
    queryKey: analyticsKeys.costTrend(params),
    queryFn: () => analyticsService.getCostTrend(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch agent metrics
 *
 * @param params - Query parameters
 * @returns Query result with agent metrics
 */
export function useAgentMetrics(params?: AnalyticsQueryParams) {
  return useQuery({
    queryKey: analyticsKeys.agents(params),
    queryFn: () => analyticsService.getAgentMetrics(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch cost breakdown by agent
 *
 * @param params - Query parameters
 * @returns Query result with cost breakdown
 */
export function useCostByAgent(params?: AnalyticsQueryParams) {
  return useQuery({
    queryKey: analyticsKeys.costByAgent(params),
    queryFn: () => analyticsService.getCostByAgent(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch recent executions
 *
 * @param limit - Number of executions to fetch
 * @returns Query result with recent executions
 */
export function useRecentExecutions(limit: number = 10) {
  return useQuery({
    queryKey: analyticsKeys.recentExecutions(limit),
    queryFn: () => analyticsService.getRecentExecutions(limit),
    staleTime: 1 * 60 * 1000, // 1 minute - more frequent updates
  });
}

/**
 * Helper function to get date range from time range
 */
export function getDateRangeFromTimeRange(timeRange: TimeRange): {
  startDate: string;
  endDate: string;
} {
  const endDate = new Date();
  const startDate = new Date();

  switch (timeRange) {
    case '7d':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(endDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(endDate.getDate() - 90);
      break;
    case '1y':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    case 'all':
      startDate.setFullYear(2020, 0, 1); // Far back enough
      break;
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

/**
 * Helper to format duration in human-readable format
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

/**
 * Helper to format cost in currency
 */
export function formatCost(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

/**
 * Helper to format percentage
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Helper to format large numbers
 */
export function formatNumber(value: number): string {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
}
